import { useEffect } from "react";
import { Link } from "react-router";
import { ChevronRight } from "lucide-react";
import { WidgetContainer } from "yeati-ui";
import { useQueryClient } from "@tanstack/react-query";
import { SummaryBody } from "@/features/purchasePoints/components/SummaryWidget/SummaryBody";
import {
  LATEST_REWARDS_COUNT,
  PURCHASE_POINTS_BRANDS_LINK_LABEL,
  PURCHASE_POINTS_BRANDS_PATH,
  PURCHASE_POINTS_PATH,
  PURCHASE_POINTS_SUMMARY_LINK_LABEL,
  PURCHASE_POINTS_TITLE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import type { PurchasePointsErrorCode } from "@/features/purchasePoints/errors/purchasePointsError";
import { usePurchasePointsBalance } from "@/features/purchasePoints/hooks/usePurchasePointsBalance";
import { usePurchasePointsRewards } from "@/features/purchasePoints/hooks/usePurchasePointsRewards";
import { readSummaryState } from "@/features/purchasePoints/utils/readSummaryState";

// Its own first page, never advanced: this is a five-gain preview, not a
// paginated list.
const FIRST_PAGE = 1;

const TITLE_ID = "purchase-points-summary-title";

const HEADER_LINK = "inline-flex shrink-0 items-center text-xs font-semibold text-primary hover:underline dark:text-primary";

// Served by the backend on a toggle made in another tab, exactly as on the
// catalogue page: the session still claims the feature is on, so the
// authorisations tab would show the switch the wrong way round.
const OPTED_OUT_CODE: PurchasePointsErrorCode = "employee_opted_out";

const SummaryHeader = () => (
  <div className="mb-4 flex flex-col gap-1">
    <div className="flex items-start justify-between gap-4">
      <h2 id={TITLE_ID} className="text-base font-bold text-gray-900 dark:text-white">{PURCHASE_POINTS_TITLE}</h2>

      <div className="flex flex-col items-end gap-1">
        <Link to={PURCHASE_POINTS_PATH} className={HEADER_LINK} data-testid="purchase-points-summary-link">
          {PURCHASE_POINTS_SUMMARY_LINK_LABEL}
          <ChevronRight size={15} aria-hidden="true" className="ms-1" />
        </Link>
        <Link to={PURCHASE_POINTS_BRANDS_PATH} className={`${HEADER_LINK} self-start`} data-testid="purchase-points-summary-brands-link">
          {PURCHASE_POINTS_BRANDS_LINK_LABEL}
          <ChevronRight size={15} aria-hidden="true" className="ms-1" />
        </Link>
      </div>
    </div>
  </div>
);

SummaryHeader.displayName = "SummaryHeader";

// Either reading may carry the refusal: a counters failure answering first
// must not hide the gains telling that the session is out of date.
const useOptedOutSessionInvalidation = (codes: (PurchasePointsErrorCode | undefined)[]) => {
  const queryClient = useQueryClient();
  const isOptedOut = codes.includes(OPTED_OUT_CODE);

  useEffect(() => {
    if (isOptedOut) queryClient.invalidateQueries({ queryKey: ["user"] });
  }, [isOptedOut, queryClient]);
};

export const PurchasePointsSummary = () => {
  const balanceReading = usePurchasePointsBalance();
  const rewardsReading = usePurchasePointsRewards(FIRST_PAGE, LATEST_REWARDS_COUNT);
  useOptedOutSessionInvalidation([balanceReading.error?.code, rewardsReading.error?.code]);

  const state = readSummaryState(balanceReading, rewardsReading);
  if (state === "hidden") return null;

  return (
    <WidgetContainer className="overflow-visible">
      <section aria-labelledby={TITLE_ID} data-testid="purchase-points-summary">
        <SummaryHeader />
        <SummaryBody
          state={state}
          balance={balanceReading.balance}
          balanceError={balanceReading.error}
          onRetryBalance={balanceReading.retry}
          rewards={rewardsReading.rewards}
          rewardsError={rewardsReading.error}
          onRetryRewards={rewardsReading.retry}
        />
      </section>
    </WidgetContainer>
  );
};

PurchasePointsSummary.displayName = "PurchasePointsSummary";
